import React, { useMemo } from "react";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import Switch from "Common/Custom/Switch";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormSelect from "Common/Custom/FormSelect";

/**
 * CommonSettingForm Component
 * Renders a dynamic form for managing site-wide configurations. 
 * Supports conditional fields based on the selected storage type.
 */
const CommonSettingForm = ({
  fields,
  values,
  errors,
  handleChange,
  handleFileChange,
  handleSubmit,
  loading,
  admin_logoPreview,
  web_logoPreview,
  LanguageOptions = []
}) => {
  // Constants for storage-specific fields to allow conditional rendering
  const STORAGE_LOCAL = ["uploadPath"];
  const STORAGE_CLOUDINARY = ["cloudName", "apiKey", "apiSecret"];
  const STORAGE_AWS = ["bucket", "region", "accessKeyId", "secretAccessKey"];

  // Filter out meta-fields from the main loop
  const filteredFields = useMemo(() =>
    fields.filter(field => field.name !== "country_code"),
    [fields]
  );

  const storageTypeOptions = [
    { value: "local", label: "Local Disk Storage" },
    { value: "cloudinary", label: "Cloudinary (SaaS)" },
    { value: "aws", label: "Amazon S3 (Cloud)" }
  ];

  return (
    <PageBody
      onSubmit={handleSubmit}
      headerchildren={<HeadingOne>System Administration Settings</HeadingOne>}
    >
      {filteredFields.map((field) => {
        // 1. File Upload Fields (Logos)
        if (field.type === "file") {
          return (
            <FormInputFile
              key={field.name}
              field={field}
              handleChange={handleFileChange}
              error={errors[field.name]}
              imagePreview={field.name === "admin_logo" ? admin_logoPreview : web_logoPreview}
            />
          );
        }

        // 2. Specialized Select Dropdowns
        if (field.type === "select") {
          let options = [];

          if (field.name === "lenguage") {
            options = LanguageOptions.map((opt) => ({
              value: opt.COLUMN_NAME,
              label: opt.COLUMN_NAME
            }));
          } else if (field.name === "storage_type") {
            options = storageTypeOptions;
          }

          return (
            <FormSelect
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              options={options}
              error={errors[field.name]}
            />
          );
        }

        // 3. International Phone Input
        if (field.type === "tel") {
          return (
            <FormPhoneInput
              key={field.name}
              field={field}
              value={values[field.name]}
              country_code={values['country_code']}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        }

        // 4. Feature Toggle Switches
        if (field.type === "switch") {
          return (
            <Switch
              key={field.name}
              values={values[field.name]}
              handleChange={handleChange}
              field={field}
              ValueOne="1"
              ValueTwo="0"
              LabelOne="Active"
              LabelTwo="Inactive"
            />
          );
        }

        // 5. Conditional Input Logic (Storage Providers)
        const isLocalField = STORAGE_LOCAL.includes(field.name);
        const isCloudinaryField = STORAGE_CLOUDINARY.includes(field.name);
        const isAwsField = STORAGE_AWS.includes(field.name);

        // Render generic fields OR fields matching the active storage provider
        const shouldRender =
          (!isLocalField && !isCloudinaryField && !isAwsField) ||
          (isLocalField && values.storage_type === "local") ||
          (isCloudinaryField && values.storage_type === "cloudinary") ||
          (isAwsField && values.storage_type === "aws");

        if (shouldRender) {
          return (
            <FormInput
              key={field.name}
              field={field}
              value={values[field.name]}
              handleChange={handleChange}
              error={errors[field.name]}
            />
          );
        }

        return null;
      })}

      <FormSubmitButton
        label="Commit Settings"
        loading={loading}
        colSize="col-12"
        align="end"
      />
    </PageBody>
  );
};

export default CommonSettingForm;
